// ================================================================================
// DASHBOARD DATA SERVICE - ADMIN ANALYTICS & METRICS
// ================================================================================
// 
// This service provides analytics data for the admin dashboard.
// It extends the existing user-service.ts with dashboard-specific queries.
//
// SECURITY: All functions require admin authentication
// PERFORMANCE: Queries are optimized with indexes and caching
// MAINTAINABILITY: Clean separation of concerns
// ================================================================================

import { supabase } from './supabaseClient'

// Types for dashboard analytics
export interface DashboardStats {
  // User Analytics
  totalUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  activeUsersToday: number;
  activeUsersThisWeek: number;
  
  // Role Distribution
  adminCount: number;
  readerCount: number;
  guestCount: number;
  
  // Subscription Distribution
  freeUsers: number;
  standardUsers: number;
  premiumUsers: number;
  bannedUsers: number;
  
  // Studio Usage Analytics
  totalStudioUses: number;
  studioUsesToday: number;
  studioUsesThisWeek: number;
  studioUsesThisMonth: number;
  topStudioUsers: UserStudioUsage[];
  
  // AI Token Usage (when implemented)
  totalTokensUsed: number;
  tokensUsedToday: number;
  tokensUsedThisWeek: number;
  tokensUsedThisMonth: number;
  averageTokensPerUser: number;
  
  // Recipe Analytics
  totalRecipes: number;
  recipesCreatedToday: number;
  recipesCreatedThisWeek: number;
  recipesCreatedThisMonth: number;
  
  // System Health
  lastDataRefresh: Date;

  // ChefCoin Statistics
  totalChefcoinsAllocated: number;
  totalChefcoinsEarned: number;   // lifetime gross earned across the platform
  totalChefcoinsSpent: number;    // lifetime gross spent across the platform
  chefcoinsEarnedToday: number;
  chefcoinsSpentToday: number;
  // totalChefcoinsSpent: number; // For future use

  // Activity tiers — derived from the accurate GREATEST last_active_at signal.
  // Counts of users whose most recent activity falls into each bucket.
  activeNowCount: number;         // < 1h
  activeTodayCount: number;       // < 24h
  activeThisWeekCount: number;    // < 7d (excluding today)
  activeThisMonthCount: number;   // < 30d (excluding this week)
  inactiveCount: number;          // > 30d

  // Derived leaderboards & lists — come "for free" from the same all-users RPC
  // result already fetched by getDashboardStats (no extra round-trip).
  recentSignups: DashboardLeaderEntry[];  // newest N users
  topSpenders: DashboardLeaderEntry[];    // top users by lifetime coins_spent
  topCreators: DashboardLeaderEntry[];    // top users by recipes_created

  // 14-day usage telemetry (recipes + chefcoin flow) — fetched with two
  // lightweight date-bucketed queries; null when the queries fail.
  activityTimeseries: ActivityDay[];
}

export interface ActivityDay {
  date: string;            // ISO yyyy-mm-dd
  recipesCreated: number;
  coinsEarned: number;
  coinsSpent: number;
}

export interface UserStudioUsage {
  userId: string;
  email: string;
  name?: string;
  studioUsesCount: number;
  lastStudioUse?: Date;
  subscription: 'free' | 'standard' | 'premium' | 'banned';
}

/**
 * Generic leaderboard entry — reuse across "top creators / spenders /
 * signups" so the dashboard can render any rank list with a single shape.
 * The `value` field carries the relevant metric (recipes created, coins
 * spent, signup recency expressed as a unix ms, etc.).
 */
export interface DashboardLeaderEntry {
  userId: string;
  email: string;
  name?: string;
  subscription: 'free' | 'standard' | 'premium' | 'banned';
  metric: number;     // the value being ranked on
  metricLabel: string; // human label for the value (e.g. "3 recipes")
  timestamp?: Date;    // optional, e.g. signup time for recent signups
}

export interface DashboardUser {
  id: string;
  email: string;
  role: 'admin' | 'reader' | 'guest';
  subscription: 'free' | 'standard' | 'premium' | 'banned';
  name?: string;
  country?: string;
  created_at: string;
  updated_at: string;
  studioUsesCount: number;
  aiTokensUsed: number;
  lastActiveAt: Date;
  recipesCreated: number;
  daysActive: number;
  chefcoins_balance: number; // ChefCoin balance for the user
  // ChefCoin transaction activity (aggregated from chefcoin_transactions).
  // Defaults to 0 when no transactions exist so far for the user.
  coinsEarned: number;       // sum of positive (grant/earn) amounts
  coinsSpent: number;        // abs sum of negative (spend) amounts
  transactionsCount: number; // total number of ChefCoin transactions
  lastTransactionAt: Date | null; // most recent ChefCoin transaction timestamp
}

// ================================================================================
// ADMIN VERIFICATION HELPER
// ================================================================================

const verifyAdminAccess = async (): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  if (!profile || profile.role !== 'admin') {
    throw new Error("Access denied: Admin role required");
  }
};

// ================================================================================
// DASHBOARD STATISTICS QUERIES
// ================================================================================

// Helper: raw row shape returned by the get_admin_users_overview RPC.
// Kept local so the rest of the service can map it to DashboardUser/etc.
interface RawUserOverviewRow {
  id: string;
  email: string;
  name: string | null;
  country: string | null;
  role: 'admin' | 'reader' | 'guest';
  subscription: 'free' | 'standard' | 'premium' | 'banned';
  created_at: string;
  updated_at: string;
  last_active_at: string | null;
  studio_uses_count: number;
  ai_tokens_used: number;
  chefcoins_balance: number;
  recipes_created: number;
  coins_earned: number;
  coins_spent: number;
  transactions_count: number;
  last_transaction_at: string | null;
  total_count: number;
}

/**
 * Calls the `get_admin_users_overview` SECURITY DEFINER RPC and returns the
 * rows as typed JavaScript objects. This is the single source of truth for the
 * users admin page — it joins auth.users.last_sign_in_at (real last auth event)
 * with profile data, recipe counts, and ChefCoin transaction aggregates.
 *
 * Falls back to a no-op empty array on permission errors so the dashboard can
 * render gracefully while we surface the error in the console.
 */
const fetchUsersOverview = async (params: {
  limit: number;
  offset: number;
  searchQuery?: string;
  roleFilter?: string;
  subscriptionFilter?: string;
  activityFilter?: string;
}): Promise<RawUserOverviewRow[]> => {
  const { data, error } = await supabase.rpc('get_admin_users_overview', {
    p_limit: params.limit,
    p_offset: params.offset,
    p_search_query: params.searchQuery ?? null,
    p_role_filter: params.roleFilter ?? 'all',
    p_subscription_filter: params.subscriptionFilter ?? 'all',
    p_activity_filter: params.activityFilter ?? 'all',
  });

  if (error) throw error;
  return (data ?? []) as RawUserOverviewRow[];
};

/**
 * Fetch ALL users via the overview RPC (no filters, generous limit). Used by
 * getDashboardStats to compute platform-wide analytics from a single accurate
 * dataset that already includes real last-sign-in timestamps.
 */
const fetchAllUsersOverview = async (): Promise<RawUserOverviewRow[]> => {
  // one round-trip, server-side join. 100k is a hard ceiling; current user base
  // is <100 and the indexed scan keeps this well under the request budget.
  return fetchUsersOverview({
    limit: 100000,
    offset: 0,
    searchQuery: '',
    roleFilter: 'all',
    subscriptionFilter: 'all',
    activityFilter: 'all',
  });
};

// Map a single overview row to the DashboardUser shape consumed by the UI.
const mapRowToDashboardUser = (row: RawUserOverviewRow): DashboardUser => {
  const createdAt = new Date(row.created_at);
  const lastActiveDate = row.last_active_at ? new Date(row.last_active_at) : createdAt;
  return {
    id: row.id,
    email: row.email,
    role: row.role,
    subscription: row.subscription,
    name: row.name ?? undefined,
    country: row.country ?? undefined,
    created_at: row.created_at,
    updated_at: row.updated_at,
    lastActiveAt: lastActiveDate,
    studioUsesCount: row.studio_uses_count ?? 0,
    aiTokensUsed: row.ai_tokens_used ?? 0,
    recipesCreated: row.recipes_created ?? 0,
    daysActive: Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24))),
    chefcoins_balance: row.chefcoins_balance ?? 0,
    coinsEarned: row.coins_earned ?? 0,
    coinsSpent: row.coins_spent ?? 0,
    transactionsCount: row.transactions_count ?? 0,
    lastTransactionAt: row.last_transaction_at ? new Date(row.last_transaction_at) : null,
  };
};

export const getDashboardStats = async (): Promise<DashboardStats> => {
  await verifyAdminAccess();

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  try {
    // Single accurate dataset joined with auth.users.last_sign_in_at.
    const rows = await fetchAllUsersOverview();

    // Recipe counts (still queried cheaply as head-only counts).
    const { count: totalRecipes } = await supabase
      .from('recipes')
      .select('*', { count: 'exact', head: true });

    const { count: recipesToday } = await supabase
      .from('recipes')
      .select('*', { count: 'exact', head: true })
      .gte('dateCreated', today.toISOString());

    const { count: recipesThisWeek } = await supabase
      .from('recipes')
      .select('*', { count: 'exact', head: true })
      .gte('dateCreated', weekAgo.toISOString());

    const { count: recipesThisMonth } = await supabase
      .from('recipes')
      .select('*', { count: 'exact', head: true })
      .gte('dateCreated', monthAgo.toISOString());

    const totalUsers = rows.length;
    const newUsersToday = rows.filter(r => new Date(r.created_at) >= today).length;
    const newUsersThisWeek = rows.filter(r => new Date(r.created_at) >= weekAgo).length;
    const newUsersThisMonth = rows.filter(r => new Date(r.created_at) >= monthAgo).length;

    // Accurate activity: based on the real last_sign_in_at joined in the RPC,
    // NOT user_profiles.updated_at (which only changes on admin edits).
    const activeUsersToday = rows.filter(r =>
      r.last_active_at && new Date(r.last_active_at) >= today
    ).length;
    const activeUsersThisWeek = rows.filter(r =>
      r.last_active_at && new Date(r.last_active_at) >= weekAgo
    ).length;

    // Role distribution
    const roleStats = rows.reduce((acc, r) => {
      acc[r.role] = (acc[r.role] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Subscription distribution
    const subscriptionStats = rows.reduce((acc, r) => {
      acc[r.subscription] = (acc[r.subscription] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Studio usage + token aggregates
    const totalStudioUses = rows.reduce((sum, r) => sum + (r.studio_uses_count || 0), 0);
    const totalTokensUsed = rows.reduce((sum, r) => sum + (r.ai_tokens_used || 0), 0);

    // ChefCoin aggregates: balance pool, lifetime earned/spent, today's flow.
    const totalChefcoinsAllocated = rows.reduce((sum, r) => sum + (r.chefcoins_balance || 0), 0);
    const totalChefcoinsEarned = rows.reduce((sum, r) => sum + (r.coins_earned || 0), 0);
    const totalChefcoinsSpent = rows.reduce((sum, r) => sum + Math.abs(r.coins_spent || 0), 0);

    const topStudioUsers: UserStudioUsage[] = [...rows]
      .sort((a, b) => (b.studio_uses_count || 0) - (a.studio_uses_count || 0))
      .slice(0, 10)
      .filter(r => (r.studio_uses_count || 0) > 0)
      .map(r => ({
        userId: r.id,
        email: r.email,
        name: r.name ?? undefined,
        studioUsesCount: r.studio_uses_count || 0,
        subscription: r.subscription,
        lastStudioUse: r.last_active_at ? new Date(r.last_active_at) : undefined,
      }));

    // Recent signups — sorted by created_at desc; reuse a generic leaderboard
    // shape so the dashboard can render any "rank list" identically.
    const recentSignups: DashboardLeaderEntry[] = [...rows]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5)
      .map(r => ({
        userId: r.id,
        email: r.email,
        name: r.name ?? undefined,
        subscription: r.subscription,
        metric: new Date(r.created_at).getTime(),
        metricLabel: new Date(r.created_at).toLocaleDateString(),
        timestamp: new Date(r.created_at),
      }));

    // Top spenders — ranked by lifetime coins_spent (already summed by the RPC).
    const topSpenders: DashboardLeaderEntry[] = [...rows]
      .sort((a, b) => Math.abs(b.coins_spent || 0) - Math.abs(a.coins_spent || 0))
      .slice(0, 5)
      .filter(r => Math.abs(r.coins_spent || 0) > 0)
      .map(r => ({
        userId: r.id,
        email: r.email,
        name: r.name ?? undefined,
        subscription: r.subscription,
        metric: Math.abs(r.coins_spent || 0),
        metricLabel: `${Math.abs(r.coins_spent || 0).toLocaleString()} coins`,
      }));

    // Top creators — ranked by recipes_created (already counted by the RPC).
    const topCreators: DashboardLeaderEntry[] = [...rows]
      .sort((a, b) => (b.recipes_created || 0) - (a.recipes_created || 0))
      .slice(0, 5)
      .filter(r => (r.recipes_created || 0) > 0)
      .map(r => ({
        userId: r.id,
        email: r.email,
        name: r.name ?? undefined,
        subscription: r.subscription,
        metric: r.recipes_created || 0,
        metricLabel: `${(r.recipes_created || 0).toLocaleString()} recipes`,
      }));

    // Activity tiers — bucket users by how recently they were last seen.
    // Derived client-side from the accurate GREATEST(...) last_active_at
    // returned by get_admin_users_overview.
    const ONE_H = 3_600_000;
    const ONE_D = ONE_H * 24;
    const SEVEN_D = ONE_D * 7;
    const THIRTY_D = ONE_D * 30;
    const nowMs = Date.now();
    let activeNowCount = 0, activeTodayCount = 0, activeThisWeekCount = 0,
        activeThisMonthCount = 0, inactiveCount = 0;
    for (const r of rows) {
      if (!r.last_active_at) { inactiveCount++; continue; }
      const ms = nowMs - new Date(r.last_active_at).getTime();
      if (ms < ONE_H) activeNowCount++;
      else if (ms < ONE_D) activeTodayCount++;
      else if (ms < SEVEN_D) activeThisWeekCount++;
      else if (ms < THIRTY_D) activeThisMonthCount++;
      else inactiveCount++;
    }

    // 14-day telemetry: recipe creations + chefcoin flow per day.
    // Two cheap date-bucketed queries — these are the only extra round-trips
    // beyond the existing overview RPC. Failures degrade gracefully (empty
    // timeseries) so a missing/locked RPC never breaks the dashboard.
    let activityTimeseries: ActivityDay[] = [];
    let chefcoinsEarnedToday = 0;
    let chefcoinsSpentToday = 0;
    try {
      activityTimeseries = await fetchActivityTimeseries(14);
      const todayEntry = activityTimeseries.find(d => d.date === today.toISOString().slice(0, 10));
      if (todayEntry) {
        chefcoinsEarnedToday = todayEntry.coinsEarned;
        chefcoinsSpentToday = todayEntry.coinsSpent;
      }
    } catch {
    }

    return {
      totalUsers,
      newUsersToday,
      newUsersThisWeek,
      newUsersThisMonth,
      activeUsersToday,
      activeUsersThisWeek,

      adminCount: roleStats.admin || 0,
      readerCount: roleStats.reader || 0,
      guestCount: roleStats.guest || 0,

      freeUsers: subscriptionStats.free || 0,
      standardUsers: subscriptionStats.standard || 0,
      premiumUsers: subscriptionStats.premium || 0,
      bannedUsers: subscriptionStats.banned || 0,

      totalStudioUses,
      studioUsesToday: 0,
      studioUsesThisWeek: 0,
      studioUsesThisMonth: 0,
      totalChefcoinsAllocated,
      totalChefcoinsEarned,
      totalChefcoinsSpent,
      chefcoinsEarnedToday,
      chefcoinsSpentToday,
      topStudioUsers,

      activeNowCount,
      activeTodayCount,
      activeThisWeekCount,
      activeThisMonthCount,
      inactiveCount,

      recentSignups,
      topSpenders,
      topCreators,

      activityTimeseries,

      totalTokensUsed,
      tokensUsedToday: 0,
      tokensUsedThisWeek: 0,
      tokensUsedThisMonth: 0,
      averageTokensPerUser: totalUsers > 0 ? totalTokensUsed / totalUsers : 0,

      totalRecipes: totalRecipes || 0,
      recipesCreatedToday: recipesToday || 0,
      recipesCreatedThisWeek: recipesThisWeek || 0,
      recipesCreatedThisMonth: recipesThisMonth || 0,

      lastDataRefresh: new Date(),
    };
  } catch {
    throw new Error('Failed to fetch dashboard statistics');
  }
};

// ================================================================================
// 14-DAY ACTIVITY TIMESERIES
// ================================================================================
// Two cheap date-bucketed queries combined into a single per-day array so the
// dashboard can render a unified "activity pulse" chart (recipe creations +
// chefcoin earnings/spending overlaid on the same time axis).
//
// Notes:
//   * Both queries are filtered to a rolling window (now - N days) and grouped
//     by day in the DB, so we only transfer tiny result sets.
//   * The ChefCoin query is admin-only by virtue of the underlying table RLS
//     policies — same access level already required to call getDashboardStats.
//   * Missing days in the window are zero-filled here so the chart x-axis is
//     continuous regardless of activity gaps.

const fetchActivityTimeseries = async (days: number): Promise<ActivityDay[]> => {
  const windowStart = new Date(Date.now() - (days - 1) * 24 * 60 * 60 * 1000);
  const windowStartIso = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate()).toISOString();

  // Recipe creations per day (UTC-bucketed via date_trunc server-side). We use
  // select + a small in-JS aggregator rather than an rpc(...) call to avoid
  // adding a new SECURITY DEFINER function for what is a single round-trip.
  const { data: recipeRows, error: recipeErr } = await supabase
    .from('recipes')
    .select('dateCreated')
    .gte('dateCreated', windowStartIso);
  if (recipeErr) throw recipeErr;

  // ChefCoin transactions per day (amounts split by sign -> earn vs spend).
  const { data: coinRows, error: coinErr } = await supabase
    .from('chefcoin_transactions')
    .select('amount, created_at')
    .gte('created_at', windowStartIso);
  if (coinErr) throw coinErr;

  // Build the full per-day window with zero-fill so the chart x-axis is stable.
  const buckets: Map<string, ActivityDay> = new Map();
  for (let i = 0; i < days; i++) {
    const d = new Date(windowStart.getTime() + i * 24 * 60 * 60 * 1000);
    const key = d.toISOString().slice(0, 10);
    buckets.set(key, { date: key, recipesCreated: 0, coinsEarned: 0, coinsSpent: 0 });
  }

  for (const row of recipeRows ?? []) {
    const key = (row.dateCreated ?? '').slice(0, 10);
    const bucket = buckets.get(key);
    if (bucket) bucket.recipesCreated++;
  }

  for (const row of coinRows ?? []) {
    const key = (row.created_at ?? '').slice(0, 10);
    const bucket = buckets.get(key);
    if (!bucket) continue;
    const amount = Number(row.amount) || 0;
    if (amount > 0) bucket.coinsEarned += amount;
    else if (amount < 0) bucket.coinsSpent += Math.abs(amount);
  }

  return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
};

// ================================================================================
// ENHANCED USER MANAGEMENT QUERIES
// ================================================================================

export const getDashboardUsers = async (
  limit = 50,
  offset = 0,
  searchQuery = '',
  roleFilter = 'all',
  subscriptionFilter = 'all',
  activityFilter = 'all'
): Promise<{ users: DashboardUser[]; totalCount: number }> => {
  await verifyAdminAccess();

  try {
    // Delegate everything (search, role/subscription/activity filtering,
    // pagination, recipe counts, and ChefCoin aggregates) to a single
    // SECURITY DEFINER RPC that joins auth.users.last_sign_in_at for an
    // accurate "last accessed" timestamp. The previous flow computed
    // `last_active_at = user_profiles.updated_at` which only flips on admin
    // edits and misreported active users as inactive.
    const rows = await fetchUsersOverview({
      limit,
      offset,
      searchQuery,
      roleFilter,
      subscriptionFilter,
      activityFilter,
    });

    // The RPC returns total_count (window aggregate) on every row. Use the
    // first row's value when present, else fall back to the page length so the
    // pagination UI doesn't divide by zero.
    const totalCount = rows[0]?.total_count ?? rows.length;
    const users = rows.map(mapRowToDashboardUser);

    return { users, totalCount };
  } catch {
    throw new Error('Failed to fetch dashboard users');
  }
};

// ================================================================================
// USER ACTIVITY TRACKING
// ================================================================================

export const updateUserActivity = async (userId: string): Promise<void> => {
  // Throttled "touch" ping. The DB-side update_user_activity() SECURITY DEFINER
  // RPC only writes user_profiles.last_active_at when the existing value is
  // older than 1 hour, so it's safe to call this on every app load + on a
  // recurring interval without write amplification. Failures are silently
  // swallowed — last_active_at is a best-effort signal, not a critical path.
  if (!userId) return;
  try {
    await supabase.rpc('update_user_activity', { p_user_id: userId });
  } catch {
  }
};

export const incrementStudioUsage = async (userId: string): Promise<void> => {
  try {
    const { error } = await supabase.rpc('increment_studio_usage', { user_id: userId });
    
    if (error) {
      // Fallback to manual increment if RPC doesn't exist
      const { data: currentUser } = await supabase
        .from('user_profiles')
        .select('studio_uses_count')
        .eq('id', userId)
        .single();

      if (currentUser) {
        await supabase
          .from('user_profiles')
          .update({ 
            studio_uses_count: (currentUser.studio_uses_count || 0) + 1
          })
          .eq('id', userId);
      }
    }
  } catch {
    // Don't throw - this is a non-critical operation
  }
};

export const incrementTokenUsage = async (userId: string, tokens: number): Promise<void> => {
  try {
    const { data: currentUser } = await supabase
      .from('user_profiles')
      .select('ai_tokens_used')
      .eq('id', userId)
      .single();

    if (currentUser) {
      await supabase
        .from('user_profiles')
        .update({ 
          ai_tokens_used: (currentUser.ai_tokens_used || 0) + tokens
        })
        .eq('id', userId);
    }
  } catch {
    // Don't throw - this is a non-critical operation
  }
};

// ================================================================================
// TOKEN ESTIMATION UTILITIES
// ================================================================================

/**
 * Estimates token usage from text content
 * Rough approximation: ~4 characters = 1 token (based on OpenAI/Gemini patterns)
 * @param prompt The input prompt text
 * @param response The AI response text  
 * @returns Estimated token count
 */
export const estimateTokenUsage = (prompt: string, response: string): number => {
  const totalChars = prompt.length + response.length;
  // Conservative estimate: 4 chars per token
  return Math.ceil(totalChars / 4);
};

/**
 * Tracks AI token usage for a user
 * @param userId User ID to track tokens for
 * @param tokenCount Number of tokens to add
 */
export const trackTokenUsage = async (userId: string, tokenCount: number): Promise<void> => {
  try {
    await supabase
      .rpc('increment_ai_tokens', { 
        user_id: userId, 
        token_count: tokenCount 
      });
  } catch {
  }
};

// ================================================================================
// DASHBOARD EXPORT FUNCTIONS
// ================================================================================

export const exportUserData = async (
  format: 'csv' | 'json' = 'csv',
  searchQuery?: string,
  roleFilter?: string,
  subscriptionFilter?: string
): Promise<string> => {
  await verifyAdminAccess();

  try {
    const { users } = await getDashboardUsers(
      1000, 
      0, 
      searchQuery || '',
      roleFilter || 'all',
      subscriptionFilter || 'all',
      'all'
    ); // Get filtered users

    if (format === 'json') {
      return JSON.stringify(users, null, 2);
    }

    // CSV format
    const headers = [
      'Email',
      'Role',
      'Subscription',
      'Name',
      'Country',
      'Created At',
      'Last Active',
      'Studio Uses',
      'AI Tokens Used',
      'Recipes Created',
      'Days Active',
      'ChefCoin Balance',
      'ChefCoins Earned',
      'ChefCoins Spent',
      'ChefCoin Transactions',
      'Last ChefCoin Tx'
    ];

    const csvRows = [
      headers.join(','),
      ...users.map(user => [
        `"${user.email}"`,
        user.role,
        user.subscription,
        `"${user.name || ''}"`,
        `"${user.country || ''}"`,
        user.created_at,
        user.lastActiveAt.toISOString(),
        user.studioUsesCount,
        user.aiTokensUsed,
        user.recipesCreated,
        user.daysActive,
        user.chefcoins_balance,
        user.coinsEarned,
        user.coinsSpent,
        user.transactionsCount,
        user.lastTransactionAt ? user.lastTransactionAt.toISOString() : ''
      ].join(','))
    ];

    return csvRows.join('\n');

  } catch {
    throw new Error('Failed to export user data');
  }
};
